import { Check, CircleHelp, X } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AddressDisplay } from '@/components/data/AddressDisplay'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { useSessionRoles, type ContractRoles } from '@/hooks/chain/useSessionRoles'
import type { OperatorRole } from '@/types/api'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const OFFCHAIN_META: Record<OperatorRole, { label: string; variant: BadgeProps['variant'] }> = {
    admin: { label: 'Admin', variant: 'positive' },
    mlro: { label: 'MLRO', variant: 'inProgress' },
    read_only: { label: 'Read-only', variant: 'muted' },
}

export function SessionRolesDialog({ open, onOpenChange }: Props) {
    const { address, roles } = useOperatorSession()
    const { chains, isLoading } = useSessionRoles(address as `0x${string}` | undefined)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Session roles</DialogTitle>
                    <DialogDescription>
                        Console roles are the admin / MLRO / read-only grants held on the on-chain{' '}
                        <code className="font-mono text-xs">OperatorRoles</code> contract; the JWT
                        caches them to gate the console's mutating UI. On-chain roles below are
                        every role the connected wallet holds directly on each contract.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <section className="space-y-2">
                        <Label>Wallet</Label>
                        {address ? (
                            <AddressDisplay address={address} truncate={false} />
                        ) : (
                            <span className="text-muted-foreground">Not connected</span>
                        )}
                    </section>

                    <section className="space-y-2">
                        <Label>Console roles (OperatorRoles)</Label>
                        <div className="flex flex-wrap gap-1.5">
                            {roles.length === 0 ? (
                                <span className="text-muted-foreground text-xs">
                                    None — this wallet holds no console role on the{' '}
                                    <code>OperatorRoles</code> contract. An admin can grant one from
                                    the Operators page.
                                </span>
                            ) : (
                                roles.map((r) => {
                                    const meta = OFFCHAIN_META[r]
                                    return (
                                        <Badge key={r} variant={meta.variant}>
                                            {meta.label}
                                        </Badge>
                                    )
                                })
                            )}
                        </div>
                    </section>

                    <section className="space-y-3">
                        <Label>On-chain roles</Label>
                        {isLoading ? (
                            <Skeleton className="h-40 w-full" />
                        ) : chains.length === 0 ? (
                            <p className="text-muted-foreground text-xs">No chains configured.</p>
                        ) : (
                            <div className="space-y-3">
                                {chains.map((c) => (
                                    <div
                                        key={c.chainId}
                                        className="border-border rounded-md border"
                                    >
                                        <div className="border-border bg-muted/40 border-b px-3 py-2 text-sm font-semibold">
                                            {c.chainName}{' '}
                                            <span className="text-muted-foreground text-xs font-normal">
                                                chain id {c.chainId}
                                            </span>
                                        </div>
                                        <div className="divide-border divide-y">
                                            {c.contracts.map((contract) => (
                                                <ContractRolesRow
                                                    key={contract.contract.name}
                                                    roles={contract}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    )
}

function Label({ children }: { children: React.ReactNode }) {
    return (
        <div className="text-muted-foreground text-[11px] tracking-wide uppercase">{children}</div>
    )
}

function ContractRolesRow({ roles }: { roles: ContractRoles }) {
    const { contract, address, roles: roleList } = roles
    return (
        <div className="flex flex-wrap items-start gap-3 px-3 py-2">
            <div className="min-w-[180px]">
                <div className="text-sm font-semibold">{contract.label}</div>
                {address ? (
                    <AddressDisplay address={address} />
                ) : (
                    <span className="text-muted-foreground text-xs italic">
                        address not configured
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-wrap gap-1.5">
                {roleList.map(({ role, held }) => {
                    if (!address) {
                        return (
                            <Badge key={role.label} variant="muted" className="gap-1">
                                <CircleHelp className="h-3 w-3" />
                                {role.label}
                            </Badge>
                        )
                    }
                    if (held === undefined) {
                        return (
                            <Badge key={role.label} variant="muted" className="gap-1">
                                <CircleHelp className="h-3 w-3" />
                                {role.label}
                            </Badge>
                        )
                    }
                    return (
                        <Badge
                            key={role.label}
                            variant={held ? 'positive' : 'outline'}
                            className="gap-1"
                        >
                            {held ? (
                                <Check className="h-3 w-3" />
                            ) : (
                                <X className="h-3 w-3 opacity-60" />
                            )}
                            {role.label}
                        </Badge>
                    )
                })}
            </div>
        </div>
    )
}
